import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from 'supabase/database.types'

type EnsureQuoteItemCanvasStubParams = {
  quoteItemId: number
  quantity: number
  decorationDescription?: string
}

type StubRow = { id: number }

const inFlightByQuoteItemId = new Map<number, Promise<StubRow>>()

/** Creates a decoration sub-item + design stub so the product appears on the request canvas. */
export const ensureQuoteItemCanvasStub = async (
  supabase: SupabaseClient<Database>,
  {
    quoteItemId,
    quantity,
    decorationDescription = 'New decoration',
  }: EnsureQuoteItemCanvasStubParams
) => {
  const pending = inFlightByQuoteItemId.get(quoteItemId)
  if (pending) return pending

  const work = (async (): Promise<StubRow> => {
    const { data: existing } = await supabase
      .from('quote_sub_item')
      .select('id')
      .eq('quote_item_id', quoteItemId)
      .eq('type', 'decoration')
      .limit(1)
      .maybeSingle()

    if (existing?.id) return existing

    const { data: subItem, error: subError } = await supabase
      .from('quote_sub_item')
      .insert({
        quote_item_id: quoteItemId,
        type: 'decoration',
        description: decorationDescription,
        quantity,
      })
      .select('id')
      .single()

    if (subError) throw subError

    const { error: designError } = await supabase.from('design').insert({
      quote_sub_item_id: subItem.id,
      w: 100,
      h: 100,
    })

    if (designError) throw designError

    return subItem
  })().finally(() => {
    inFlightByQuoteItemId.delete(quoteItemId)
  })

  inFlightByQuoteItemId.set(quoteItemId, work)
  return work
}
